import { useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import ProductIcon from '../icons/ProductIcon'
import { OpsButton } from './ops'
import { useModalFocus } from './modalFocus'

const cx = (...parts) => parts.filter(Boolean).join(' ')

const WIDTHS = { md: 'min(560px, calc(100vw - 32px))', lg: 'min(800px, calc(100vw - 32px))' }

/**
 * A modal dialog without CoreUI. Focus moves in on open, stays inside (and
 * cycles with a Guide tour panel when one is on the page), Escape closes, and
 * focus returns to whatever opened it. `footer` holds the buttons, right
 * aligned: Cancel, then the commit, the verb repeating the object (decision 11).
 * OpsModal stays as it is for its existing callers.
 */
export default function Dialog({ open, title, onClose, footer, size = 'md', children, returnFocusRef, className }) {
  const titleId = useId()
  const panelRef = useRef(null)
  useModalFocus({ open, panelRef, onClose, returnFocusRef, returnToOpener: true })

  if (!open) return null
  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 z-[1055] flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--slate-12) 40%, transparent)', overscrollBehavior: 'contain' }}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx('flex max-h-[85vh] flex-col rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-overlay)] text-[var(--app-text)]', className)}
        style={{ width: WIDTHS[size] ?? WIDTHS.md, boxShadow: 'var(--app-shadow-2)' }}
      >
        <header className="flex items-center justify-between gap-3 border-b border-[var(--app-border)] px-5 py-3">
          <h2 id={titleId} className="ops-heading m-0 text-[16px] font-medium" style={{ textWrap: 'balance' }}>{title}</h2>
          <OpsButton tone="quiet" size="sm" aria-label="Close" onClick={onClose}>
            <ProductIcon name="ui/x" size="inline" />
          </OpsButton>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-[14px]">{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-[var(--app-border)] px-5 py-3">{footer}</footer>}
      </section>
    </div>,
    document.body,
  )
}
